import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  usePollWorkflowRunUntilCompleted,
  useTriggerWorkflowWithRunIdPoll,
  useWorkflowRuns,
} from "Hooks/Github";
import { useSnackbar } from "notistack";
import React, { SyntheticEvent, useMemo, useState } from "react";
import { ENV_FULL_NAME } from "Types/env";

import MpsDemoScriptStepper from "./Stepper";

const imaqplannerTag = "imaqplanner";
const iosplannerTag = "iosplanner";

enum MPS_SCRIPT_TARGET {
  imaqplanner = "IMAQ Planner",
  iosplanner = "IOS Planner",
}

const mpsDemoTargetOptions = [
  {
    value: MPS_SCRIPT_TARGET.imaqplanner,
    tooltip:
      "IMAQ Planner setup, use of 'imaqplanner' tag for satellite, ground stations, orbit, activity tags",
  },
  {
    value: MPS_SCRIPT_TARGET.iosplanner,
    tooltip:
      "IOS Planner setup, use of 'iosplanner' tag for satellite, ground stations, orbit, activity tags",
  },
];

enum MPS_SCRIPT_MODE {
  STANDARD = "Standard",
  GMAT_INTEGRATION = "GMAT integration",
  NO_ORBITAL_EVENTS = "No orbital events",
  SETUP_EXOTRAIL_CONNECTION = "Setup exotrail connection",
  CLEANUP = "Cleanup",
}

const mpsModeOptions = [
  {
    value: MPS_SCRIPT_MODE.STANDARD,
    tooltip:
      "Orbital events, contacts, activities, draft plan and committed plan are created and a resource propagation is triggered for each plan",
  },
  {
    value: MPS_SCRIPT_MODE.GMAT_INTEGRATION,
    tooltip:
      "GMAT script integration mode, STANDARD but no orbital events are generated and an existing DRAFT plan is used",
  },
  {
    value: MPS_SCRIPT_MODE.NO_ORBITAL_EVENTS,
    tooltip: "STANDARD but no orbital events nor contacts are generated",
  },
  {
    value: MPS_SCRIPT_MODE.SETUP_EXOTRAIL_CONNECTION,
    tooltip: "Only a connection to exotrail will be created",
  },
  {
    value: MPS_SCRIPT_MODE.CLEANUP,
    tooltip:
      "Activities related to the satellite are deleted and all the plans with the given tag are deleted",
  },
];

const MpsDemoScript: React.FC = ({}) => {
  const repositoryName = "mps-demo-script";
  const workflowId = "run-with-release-old-mps-demo-script.yml";

  const { enqueueSnackbar } = useSnackbar();
  const [environment, setEnvironment] = useState<ENV_FULL_NAME>(
    ENV_FULL_NAME.DEV
  );

  const [mode, setMode] = useState<MPS_SCRIPT_MODE>(MPS_SCRIPT_MODE.STANDARD);
  const [demoTarget, setDemoTarget] = useState<MPS_SCRIPT_TARGET>(
    MPS_SCRIPT_TARGET.imaqplanner
  );

  const computedTags = useMemo(() => {
    switch (demoTarget) {
      case MPS_SCRIPT_TARGET.imaqplanner: {
        return {
          satelliteTag: imaqplannerTag,
          groundStationsTag: imaqplannerTag,
          orbitTag: imaqplannerTag,
          activityTag: imaqplannerTag,
          planTag: imaqplannerTag,
        };
      }
      case MPS_SCRIPT_TARGET.iosplanner: {
        return {
          satelliteTag: iosplannerTag,
          groundStationsTag: iosplannerTag,
          orbitTag: iosplannerTag,
          activityTag: iosplannerTag,
          planTag: iosplannerTag,
        };
      }
      default: {
        return {
          satelliteTag: imaqplannerTag,
          groundStationsTag: imaqplannerTag,
          orbitTag: imaqplannerTag,
          activityTag: imaqplannerTag,
          planTag: imaqplannerTag,
        };
      }
    }
  }, [demoTarget]);

  const [isWorkflowTriggerButtonDisabled, setIsWorkflowTriggerButtonDisabled] =
    useState<boolean>(false);
  const [workflowRunId, setWorkflowRunId] = useState<number | undefined>(
    undefined
  );
  const [isWaitingForNewRun, setIsWaitingForNewRun] = useState(false);

  const { mutateAsync: triggerWorkflow } = useTriggerWorkflowWithRunIdPoll();
  const { refetch, latestRunId } = useWorkflowRuns(
    repositoryName,
    workflowId,
    isWaitingForNewRun
  );

  const runIdToUse = workflowRunId ?? latestRunId;

  const { data: run, dataUpdatedAt: lastRunFetch } =
    usePollWorkflowRunUntilCompleted(repositoryName, runIdToUse);

  const onRunLaunch = async (e: SyntheticEvent) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    setIsWaitingForNewRun(true);
    setIsWorkflowTriggerButtonDisabled(true);

    try {
      const newRunId = await triggerWorkflow({
        workflow_id: workflowId,
        repo: repositoryName,
        ref: "main",
        inputs: {
          token: form.leanspacetoken.value,
          environment: environment,
          ...computedTags,
          mode: Object.keys(MPS_SCRIPT_MODE)[
            Object.values(MPS_SCRIPT_MODE).indexOf(mode)
          ], // get the key of the enum
        },
      });

      setWorkflowRunId(newRunId);
      setIsWaitingForNewRun(false);
      setIsWorkflowTriggerButtonDisabled(false);

      enqueueSnackbar(
        "MPS demo script application has been requested, its state will soon be displayed in the progress bar",
        { variant: "success" }
      );
    } catch (err) {
      setIsWaitingForNewRun(false);
      setIsWorkflowTriggerButtonDisabled(false);
      enqueueSnackbar(`Failed to trigger workflow: ${err}`, {
        variant: "error",
      });
    }
  };

  const onWorkflowRefetch = () => {
    refetch();
  };

  return (
    <>
      <Box
        sx={{
          padding: 2,
        }}
      >
        <Typography sx={{ padding: 2 }} variant="h5">
          MPS Demo script (before SmallSat 2025)
        </Typography>
        <Typography sx={{ padding: 2 }}>Connection settings</Typography>
        <form onSubmit={onRunLaunch}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              padding: 2,
            }}
          >
            <Tooltip title="Leanspace JWT token, found in the console (settings>API)">
              <TextField
                id="leanspacetoken"
                name="leanspacetoken"
                required
                label="Leanspace token"
                sx={{ paddingRight: 10 }}
              />
            </Tooltip>
            <Tooltip title="On which environment data is generated">
              <FormControl required sx={{ paddingRight: 10 }}>
                <FormLabel id="environment">Environment</FormLabel>
                <RadioGroup
                  row
                  defaultValue={ENV_FULL_NAME.DEV}
                  onChange={(event) =>
                    setEnvironment(event.target.value as ENV_FULL_NAME)
                  }
                >
                  <FormControlLabel
                    value={ENV_FULL_NAME.DEV}
                    control={<Radio />}
                    label={ENV_FULL_NAME.DEV}
                    labelPlacement="bottom"
                  />
                  <FormControlLabel
                    value={ENV_FULL_NAME.PROD}
                    control={<Radio />}
                    label={ENV_FULL_NAME.PROD}
                    labelPlacement="bottom"
                  />
                </RadioGroup>
              </FormControl>
            </Tooltip>
          </Box>
          <Box>
            <FormControl required sx={{ paddingRight: 10 }}>
              <FormLabel id="mpsScriptTarget">Demo to setup</FormLabel>
              <RadioGroup
                row
                defaultValue={MPS_SCRIPT_TARGET.imaqplanner}
                onChange={(event) =>
                  setDemoTarget(event.target.value as MPS_SCRIPT_TARGET)
                }
              >
                {mpsDemoTargetOptions.map((option) => (
                  <Tooltip
                    key={option.value}
                    title={
                      <Typography variant="body2">{option.tooltip}</Typography>
                    }
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={option.value}
                      labelPlacement="bottom"
                    />
                  </Tooltip>
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl required>
              <FormLabel id="mpsScriptMode">MPS Script Mode</FormLabel>
              <RadioGroup
                row
                defaultValue={MPS_SCRIPT_MODE.STANDARD}
                onChange={(event) =>
                  setMode(event.target.value as MPS_SCRIPT_MODE)
                }
              >
                {mpsModeOptions.map((option) => (
                  <Tooltip
                    key={option.value}
                    title={
                      <Typography variant="body2">{option.tooltip}</Typography>
                    }
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={option.value}
                      labelPlacement="bottom"
                    />
                  </Tooltip>
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ padding: 2, textAlign: "right" }}>
            <Button
              variant="contained"
              sx={{ marginRight: 10 }}
              onClick={onWorkflowRefetch}
            >
              Re-fetch last workflow run
            </Button>
            <Button
              disabled={isWorkflowTriggerButtonDisabled}
              variant="contained"
              type="submit"
            >
              Execute script
            </Button>
          </Box>
        </form>
      </Box>

      {isWaitingForNewRun ? (
        <Box sx={{ textAlign: "center", padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        run && <MpsDemoScriptStepper run={run} lastRunFetch={lastRunFetch} />
      )}
    </>
  );
};

export default MpsDemoScript;
